/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// tslint:disable max-file-line-count
// Core Libraries
import { Component, Input, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { Wove } from 'aspect.js/';

// Services
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

// Interfaces
import { ProductSelectorConfigData } from '@core/aem/interfaces/product-selector/product-selector.interface';
import { Cart } from '@support/api/proxies/responses/api-cart-response.interface';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { PRODUCT_OFFERINGS_CONSTANTS } from '@core/aem/components/product-offerings/product-offerings.constants';
import { ActivatedRoute, Router } from '@angular/router';
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { WorkflowService } from '@root/shared/services/workflow.service';
import { WORKFLOW } from '@root/shared/constants/workflow.constants';
import { AddDeviceToNonLineItemsRequestModel } from 'app/model/add-nonline-device-request.model';
import { takeUntil } from 'rxjs/operators';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { PdlWrapperService } from '@core/services/pdl-wrapper.service';
import { ProductInfo } from '@support/shared/interfaces/pdl-data.interface';
import {
  CART_EVENT_NAME,
  PDP_SUBTYPE_CONSTANT,
} from '@support/shared/constants/pdl-data.constants';

@Wove({ moduleName: PRODUCT_OFFERINGS_CONSTANTS.TILE_MANAGEMENT_SERVICE })
@Component({
  selector: 'tmo-add-remove-line',
  templateUrl: './add-remove-line.component.html',
})
export class AddRemoveLineComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   * Stores AEM data for add/remove Section
   */
  @Input() public aemContent: ProductSelectorConfigData;

  /**
   * varible to check line status
   */
  @Input() public isLineCompleted: boolean;
  /**
   * varible to check focused tileId details
   */
  @Input() public focusedTileIdDetails: LineDeivceDetails;
  /**
   * varible to check all complete and not complete tile details
   */
  @Input() public allLineDetails: LineDeivceDetails[];

  /**
   * Holds boolean to removed non complete line
   * @param {boolean} removeLine
   */
  @Input() public set removeInCompleteLine(removeLine: boolean) {
    if (removeLine) {
      this.removeLine();
    }
  }
  /**
   * Flag to see open or close modal
   */
  public openLinesModal: boolean;

  /**
   * model to store dynamic data
   */
  public modal: DynamicModal;

  /**
   * Boolean to disable addRemove buttons if current line does not have correct lineID set
   */
  public disableRemoveTileCTA: boolean;

  /**
   * Boolean to disable Add Line CTA if line does not have plan selected
   */
  public disableAddTileCTA: boolean;

  /**
   * Event Emitter to trigger events on click of anchor
   */
  public triggerEvent: EventEmitter<Object>;
  /**
   * Holds boolean to check if (currentVoiceCount >= maxVoiceCount);
   */
  public isValidToAddALine: boolean;
  /**
   * Holds max voice lines
   */
  public maxVoiceLines: number;
  /**
   * Holds boolean if user has tourist plan
   */
  public isTouristPlan: boolean;

  @Output() public getUpdatedLineCount: EventEmitter<boolean>;
  /**
   * Holds boolean to enable or disable to show spinner
   */
  public showSpinner: boolean;

  /**
   * Holds boolean to enable to disable duplicate button
   */
  public isDuplicateButtonDisabled: boolean;
  /**
   * Holds boolean to enable to disable remove button
   */
  public isRemoveButtonDisabled: boolean;
  /**
   * Holds boolean disableAddALine
   */
  public disableAddALine: boolean;
  /**
   * Cart Object
   */
  public cartReference: Cart;
  /**
   * Holds boolean if user has test drive plan
   */
  public isTestDrivePlan: boolean;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Hold pdl related details to trigger event
   */
  public pdlData: object;
  /**
   * flag for channel check
   */
  // public isWebChannel: boolean;

  public productInfo: ProductInfo[];
  /**
   * flag to check whether modalName Needed or not on CartAdd event
   */
  public isModalNameNeeded: boolean;
  constructor(
    private stateStoreUtilsService: StateStoreUtilsService,
    private tileManagementService: TileManagementService,
    private router: Router,
    private cartSupportService: CartSupportService,
    private objectService: ObjectService,
    private workflowService: WorkflowService,
    private pdpDataHandlerService: PdpDataHandlerService,
    public pdlService: PdlService,
    public activatedRoute: ActivatedRoute,
    public pdlWrapperService: PdlWrapperService
  ) {
    super(pdlService, activatedRoute);
    this.getUpdatedLineCount = new EventEmitter<boolean>();
    this.modal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'add-remove-line-modal',
    };
    this.ngUnsubscribe = new Subject<void>();
    this.openLinesModal = false;
    // this.isWebChannel = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB;
  }

  /**
   * Loads the initial configuration of Add-Remove Line Section on Product Selector Page
   * @returns void
   */
  public ngOnInit(): void {
    this.getUpdatedFocusedTileDetails();
    this.disableAddCTA();
    this.disableDuplicateButton();
    this.disableRemoveButton();
    this.isTouristPlan = this.cartSupportService.isTouristPlanInCart();
  }

  /**
   * Showing of modal on toggle event
   * @param event : toggle event
   * @returns void
   */
  public showRemoveLineModal(event?: EventEmitter<Object>, isRemoveLine?: boolean): void {
    this.triggerEvent = event;
    this.openLinesModal = true;
    this.modal.toggleModalEmitter.emit({ open: true, close: false });
    // if (this.isWebChannel) {
    this.modal.modalName = this.aemContent.removeLineConfirmation;
    this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'modalLoad');
    // }
  }

  /**
   * Method to handle Click event
   * @returns void
   */
  public closeModal(): void {
    // Get the focus/keyboard accessibility back on element that opens the popUp
    const target = 'target';
    this.triggerEvent[target].focus();
    this.modal.toggleModalEmitter.emit({ open: false, close: true });
    this.openLinesModal = false;
  }

  /**
   * Method to remove selected Line and navigate to product-selector
   * @returns void
   */
  public removeLine(): void {
    this.showSpinner = true;
    const isLineTile =
      this.focusedTileIdDetails.lineTileType === lineTypeEnum.LINE &&
      !!this.objectService.get(this.focusedTileIdDetails, 'plansFromCart.id');
    const isNonLineTile =
      !!this.objectService.get(this.focusedTileIdDetails, 'deviceDetails.id') &&
      this.focusedTileIdDetails.lineTileType === lineTypeEnum.NONLINE;
    switch (true) {
      case isLineTile:
        this.removeLineTile();
        break;
      case isNonLineTile:
        this.removeNonLineTile();
        break;
      default:
        this.processLineDeletion();
        break;
    }
    this.closeModal();
  }
  /**
   * Function used to remove Line Tile
   */
  private removeLineTile(): void {
    let products;
    // if (this.isWebChannel) {
    const cartRef = this.stateStoreUtilsService.getCart();
    const lineNumber = this.objectService.get(this.focusedTileIdDetails, 'tileId').slice(-1);
    products = this.pdpDataHandlerService.getRemovedItemDetails(
      PDP_SUBTYPE_CONSTANT.LINENUMBER,
      lineNumber,
      cartRef
    );
    // }
    this.cartSupportService
      .deleteFocusedLine(this.objectService.get(this.focusedTileIdDetails, 'tileId'))
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (cart: Cart) => {
          this.processLineDeletion(cart);
          this.triggerCartEvent(
            cart,
            products,
            CART_EVENT_NAME.CARTREMOVE,
            this.aemContent.continue,
            this.modal.modalName
          );
        },
        () => {
          this.showSpinner = false;
        }
      );
  }

  /**
   * Function to remove line
   * @param cart
   * @returns void
   */
  private processLineDeletion(cart: Cart = null): void {
    this.tileManagementService.deleteLine();
    if (cart) {
      this.tileManagementService.mapToCartResponse(cart);
    }
    const tileLength = this.stateStoreUtilsService.getTileIdList(lineTypeEnum.LINE).length;
    if (tileLength === 0) {
      this.tileManagementService.createTile();
    }
    this.tileManagementService.getUpdatedData(tileLength === 1 || tileLength === 0);
    this.router.navigate([
      PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR,
      this.stateStoreUtilsService.getFocusedTileId(),
    ]);
    this.showSpinner = false;
  }

  /**
   * Function used to remove nonLineTile
   */
  private removeNonLineTile(): void {
    let products;
    // if (this.isWebChannel) {
    const cart = this.stateStoreUtilsService.getCart();
    products = this.pdpDataHandlerService.getRemovedItemDetails(
      PDP_SUBTYPE_CONSTANT.ID,
      this.objectService.get(this.focusedTileIdDetails, 'selectedDevice.id'),
      cart
    );
    // }
    this.cartSupportService
      .deleteCartLevelDevice(
        this.objectService.get(this.focusedTileIdDetails, 'selectedDevice.cartItemId')
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (value: Cart) => {
          this.showSpinner = false;
          if (this.allLineDetails.length === 1) {
            this.tileManagementService.createTile();
          }
          this.tileManagementService.getUpdatedData();
          this.triggerCartEvent(
            value,
            products,
            CART_EVENT_NAME.CARTREMOVE,
            this.aemContent.continue,
            this.modal.modalName
          );
          this.router.navigate([
            PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR,
            this.stateStoreUtilsService.getFocusedTileId(),
          ]);
        },
        () => {
          this.showSpinner = false;
        }
      );
  }
  /**
   * Method to add a new Line
   * @returns void
   */
  public addTile(): void {
    if (this.isValidToAddALine) {
      this.tileManagementService.createTile();
      this.tileManagementService.getUpdatedData(true);
    } else {
      this.tileManagementService.createTile(lineTypeEnum.NONLINE, true);
      this.tileManagementService.getUpdatedData();
    }
    const tileId = this.stateStoreUtilsService.getFocusedTileId();
    this.router.navigate([PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR, tileId]);
  }

  /**
   * Function to duplicate line
   */
  public duplicateThisLine(): void {
    this.showSpinner = true;
    if (this.objectService.get(this.focusedTileIdDetails, 'plansFromCart.id')) {
      this.duplicateLineItem();
    } else {
      this.duplicateNonLineItem();
    }
  }

  /**
   * Function used to duplicate line item
   */
  private duplicateLineItem(): void {
    // if (this.isWebChannel) {
    const cart = this.stateStoreUtilsService.getCart();
    this.productInfo = this.pdpDataHandlerService.createProductInfoForCart(cart);
    // }

    this.cartSupportService
      .duplicateExistingLine()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response: Cart) => {
          this.showSpinner = false;
          const tileId = this.stateStoreUtilsService.getFocusedTileId();
          this.router.navigate([PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR, tileId]);
          this.tileManagementService.mapToCartResponse(response);
          this.tileManagementService.getUpdatedData(true);
          this.triggerCartEvent(
            response,
            this.productInfo,
            CART_EVENT_NAME.CARTADD,
            this.aemContent.duplicate
          );
        },
        () => {
          this.showSpinner = false;
        }
      );
  }

  /**
   * Function used to duplpicate lon-line item
   */
  private duplicateNonLineItem(): void {
    const skuItemCollection = [];
    const isUsku = this.focusedTileIdDetails.selectedDevice.uSkuIndicator;
    const skuItem: AddDeviceToNonLineItemsRequestModel = new AddDeviceToNonLineItemsRequestModel();
    skuItem.setSkuDetails(
      this.focusedTileIdDetails.selectedDevice.sku,
      this.focusedTileIdDetails.selectedDevice.offerId
    );
    skuItemCollection.push(skuItem.getDeviceInfo());
    // if (this.isWebChannel) {
    const cart = this.stateStoreUtilsService.getCart();
    this.productInfo = this.pdpDataHandlerService.createProductInfoForCart(cart);
    // }
    this.cartSupportService
      .postDeviceAsNonLineItem(skuItemCollection, null, null, null, null, isUsku)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (cartData: Cart) => {
          this.tileManagementService.createTile(lineTypeEnum.NONLINE, true);
          this.tileManagementService.setNonLineItemDetails(cartData);
          this.tileManagementService.getUpdatedData();
          this.showSpinner = false;
          this.navigateToProductSelector();
          this.triggerCartEvent(
            cartData,
            this.productInfo,
            CART_EVENT_NAME.CARTADD,
            this.aemContent.duplicate
          );
        },
        () => {
          this.showSpinner = false;
        }
      );
  }
  /**
   * Function used to navigate to current tile
   */
  private navigateToProductSelector(): void {
    const nonItemTileIdList = this.stateStoreUtilsService.getTileIdList(lineTypeEnum.NONLINE);
    const lastTileIdNumber = nonItemTileIdList.slice(-1)[0];
    this.stateStoreUtilsService.setFocusedTileId(lastTileIdNumber);
    this.router.navigate([
      PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR,
      lastTileIdNumber,
    ]);
  }
  /**
   * Cleans component on lifecycle hook
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.openLinesModal = false;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Function used to get focused tile id details
   */
  private getUpdatedFocusedTileDetails(): void {
    this.tileManagementService.focusedTileDetails
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: LineDeivceDetails) => {
        if (data) {
          this.focusedTileIdDetails = data;
          this.cartReference = this.stateStoreUtilsService.getCart();
          this.maxVoiceLines = this.objectService.get(
            this.cartReference,
            'linesTotal',
            this.stateStoreUtilsService.getMaxLineDetails().maxVoiceLines
          );
          this.disableAddCTA();
          this.disableDuplicateButton();
          this.disableRemoveButton();
        }
      });
  }
  /**
   * Function triggers update of lineId in focus to last line in cart
   */
  public disableAddCTA(): void {
    this.isTestDrivePlan = this.cartSupportService.checkTestDrivePlanInCart();
    const isMaxLinesReached: boolean =
      this.objectService.get(this.cartReference, 'linesRemaining') <= 0;
    const isByos = this.cartSupportService.isByosFlow();
    this.isValidToAddALine =
      this.focusedTileIdDetails.lineTileType === lineTypeEnum.LINE
        ? !(isMaxLinesReached && !isByos)
        : !isMaxLinesReached;
    this.disableAddALine =
      isMaxLinesReached && this.objectService.get(this.focusedTileIdDetails, 'isLineSetupCompleted')
        ? isByos
        : !this.objectService.get(this.focusedTileIdDetails, 'isLineSetupCompleted');
  }

  /**
   * function used to enable or disable remove button
   * @returns {boolean}
   */
  public disableRemoveButton(): void {
    this.isRemoveButtonDisabled = this.focusedTileIdDetails.lineTileType === lineTypeEnum.EXISTING;
  }
  /**
   * Function used to enable or disable duplicate button.
   * @returns {boolean}
   */
  public disableDuplicateButton(): void {
    const isExisting = this.focusedTileIdDetails.lineTileType === lineTypeEnum.EXISTING;
    this.isDuplicateButtonDisabled =
      this.objectService.get(this.focusedTileIdDetails, 'lineTileType') === lineTypeEnum.LINE
        ? this.objectService.get(this.cartReference, 'linesRemaining') === 0 || isExisting
        : !this.objectService.get(this.focusedTileIdDetails, 'noPlanNeeded');
  }

  /**
   * Function to update workflow information
   */
  public updateWorkflow(): void {
    this.workflowService.setWorkflowIdOnEvent(WORKFLOW.NEW_FAN);
  }

  /**
   * Method to set data for cart events and trigger cart events (cartRemove, cartAdd)
   * @returns void
   */
  private triggerCartEvent(
    cart?: Cart,
    productInfo?: ProductInfo[],
    action?: string,
    ctaName?: string,
    modalName?: string
  ): void {
    // if (this.isWebChannel) {
    this.pdlData = {
      ctaName: ctaName,
      product: productInfo,
    };
    if (modalName) {
      this.isModalNameNeeded = false;
      this.objectService.set(this.pdlData, 'modalName', modalName);
    } else {
      this.isModalNameNeeded = true;
    }
    const isCartEmpty = !this.cartSupportService.isCartNotEmpty(cart);
    this.pdpDataHandlerService.triggerCartEvent(cart, this.pdlPageComponentId, isCartEmpty, action);
    // }
  }
}
